import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useGetStatusMetrics } from '../hooks/use-get-status-metrics.tsx';
import {
  DEPLOY_DETAILS_PATH,
  DEPLOY_LIST_PATH,
  TRANSACTION_DETAILS_PATH,
  TRANSACTION_LIST_PATH,
} from '../router/paths.tsx';
import { NetworkMajorVersion } from '@block-explorer/entities';
import { setNodeLunchSpotlightIgnored } from '../redux/feature-spotlights';
import { useDispatch } from 'react-redux';
import { isFuture } from 'date-fns';

type CasperNetworkContextType = {
  networkMajorVersion: NetworkMajorVersion;
  getRouteForNetworkVersion: (isDetails: boolean) => string;
  getNetworkVersionAsync: () => Promise<string>;
  setNetworkVersion?: (version: number) => void;
  getNetworkVersion?: () => string;
};
const CasperNetworkContext = createContext<CasperNetworkContextType>({} as any);

const { Provider: CasperNetworkContextProvider } = CasperNetworkContext;

export const useGetCasperNetwork = () => {
  return useContext(CasperNetworkContext);
};

/**
 * Context for migration purposes
 * remove, after 2.0.0 in mainnet
 * @param props
 */
export const CasperNetworkProvider = (props) => {
  const dispatch = useDispatch();
  const { data } = useGetStatusMetrics();
  const networkMajorVersion = data?.casperNetworkVersion;

  const version =
    (networkMajorVersion && networkMajorVersion.startsWith('1')
      ? NetworkMajorVersion.V1
      : NetworkMajorVersion.V2) || NetworkMajorVersion.V2;

  const [networkVersion, setNetworkVersion] = useState<string>(version);

  useEffect(() => {
    const isFutureNodeLunchDate = isFuture(
      new Date(config.spotlight_node_lunch_release_date)
    );

    if (
      networkMajorVersion &&
      networkMajorVersion.startsWith('2') &&
      !isFutureNodeLunchDate
    ) {
      dispatch(setNodeLunchSpotlightIgnored(false));
    }

    setNetworkVersion(version);
  }, [data?.casperNetworkVersion]);

  const getNetworkVersion = (): string => {
    return networkVersion;
  };

  const getNetworkVersionAsync = async (): Promise<string | undefined> => {
    const version = await data?.casperNetworkVersion;

    return version;
  };

  const getRouteForNetworkVersion = (isDetails: boolean): string => {
    if (version === NetworkMajorVersion.V1) {
      return isDetails ? DEPLOY_DETAILS_PATH : DEPLOY_LIST_PATH;
    }

    return isDetails ? TRANSACTION_DETAILS_PATH : TRANSACTION_LIST_PATH;
  };

  return (
    <CasperNetworkContextProvider
      value={{
        networkMajorVersion: networkVersion,
        getNetworkVersion,
        getRouteForNetworkVersion,
        getNetworkVersionAsync,
      }}
      {...props}
    />
  );
};
